import type { ProjectRole, SessionUser } from '@forge/shared';
import { defineAbilityFor, roleAtLeast, type Action, type AppAbility, type SubjectName } from '@forge/shared';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as apiClient from './api';

interface AuthContextValue {
  user: SessionUser | null;
  ability: AppAbility;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  /** The caller's role in a project, or null when they are not a member. */
  roleIn: (projectId: string | undefined) => ProjectRole | null;
  /** Capability check scoped to a project. */
  can: (action: Action, subject: SubjectName, projectId?: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(apiClient.getCurrentUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = apiClient.onAuthChange(setUser);
    void apiClient.restoreSession().finally(() => setLoading(false));
    return unsubscribe;
  }, []);

  // Keep the access token fresh while the tab is open so long editing sessions
  // never fail mid-save. The refresh cookie rotates on every call.
  useEffect(() => {
    if (!user) return;
    const timer = window.setInterval(
      () => {
        void apiClient.refreshSession();
      },
      10 * 60 * 1000,
    );
    return () => window.clearInterval(timer);
  }, [user]);

  const ability = useMemo(() => defineAbilityFor(user), [user]);

  const value = useMemo<AuthContextValue>(() => {
    const roleIn = (projectId: string | undefined): ProjectRole | null => {
      if (!projectId || !user) return null;
      if (user.globalRole === 'superadmin') return 'owner';
      return user.memberships.find((m) => m.projectId === projectId)?.role ?? null;
    };

    return {
      user,
      ability,
      loading,
      roleIn,
      can: (action, subject, projectId) => {
        if (!user) return false;
        if (user.globalRole === 'superadmin') return true;
        if (!projectId) return ability.can(action, subject);
        const record = subject === 'Project' ? { id: projectId } : { projectId };
        return ability.can(action, { ...record, __caslSubjectType__: subject } as never);
      },
      login: async (email, password) => {
        setUser(await apiClient.login(email, password));
      },
      register: async (name, email, password) => {
        setUser(await apiClient.register(name, email, password));
      },
      logout: async () => {
        await apiClient.logout();
        setUser(null);
      },
      reload: async () => {
        setUser(await apiClient.reloadUser());
      },
    };
  }, [user, ability, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience for the very common "is this person at least an editor?" check. */
export function useProjectRole(projectId: string | undefined): {
  role: ProjectRole | null;
  isViewer: boolean;
  canEdit: boolean;
  canAdmin: boolean;
  isOwner: boolean;
} {
  const { roleIn } = useAuth();
  const role = roleIn(projectId);
  return {
    role,
    isViewer: role === 'viewer',
    canEdit: roleAtLeast(role, 'editor'),
    canAdmin: roleAtLeast(role, 'admin'),
    isOwner: roleAtLeast(role, 'owner'),
  };
}
